/**
 * MonitorCreationWizard - Multi-step wizard for creating directory monitors
 *
 * Guides users through the monitor creation process:
 * 1. Basic Info - Name, description, target dataset
 * 2. Directory Config - Directory path and validation
 * 3. File Patterns - Include/exclude patterns and recursive flag
 * 4. Schedule - Scan frequency, monitor type, auto-ingest settings
 * 5. Review - Final review and monitor creation
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useMonitorCreationStore,
  MonitorCreationStep,
} from '@/features/monitors/store/monitorCreationStore';
import { BasicInfoStep } from './BasicInfoStep';
import { DirectoryConfigStep } from './DirectoryConfigStep';
import { PartitionMappingStep } from './PartitionMappingStep';
import { FilePatternsStep } from './FilePatternsStep';
import { ScheduleConfigStep } from './ScheduleConfigStep';
import { MonitorReviewStep } from './MonitorReviewStep';

/**
 * Wizard step configuration
 */
const WIZARD_STEPS = [
  {
    id: MonitorCreationStep.BASIC_INFO,
    title: 'Basic Info',
    description: 'Name and target dataset',
  },
  {
    id: MonitorCreationStep.DIRECTORY_CONFIG,
    title: 'Directory',
    description: 'Directory path configuration',
  },
  {
    id: MonitorCreationStep.PARTITION_MAPPING,
    title: 'Partitions',
    description: 'Map subdirectories to partitions',
  },
  {
    id: MonitorCreationStep.FILE_PATTERNS,
    title: 'File Patterns',
    description: 'Include and exclude patterns',
  },
  {
    id: MonitorCreationStep.SCHEDULE,
    title: 'Schedule',
    description: 'Scan frequency and timing',
  },
  {
    id: MonitorCreationStep.REVIEW,
    title: 'Review',
    description: 'Review and create monitor',
  },
];

/**
 * MonitorCreationWizard Component
 *
 * Multi-step wizard for creating directory monitors with configuration,
 * file pattern setup, scheduling, and final review.
 *
 * @example
 * ```tsx
 * <MonitorCreationWizard />
 * ```
 */
export const MonitorCreationWizard: React.FC = () => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const store = useMonitorCreationStore();

  /**
   * Calculate progress percentage
   */
  const progressPercent = ((store.currentStep + 1) / WIZARD_STEPS.length) * 100;

  /**
   * Get current step configuration
   */
  const currentStepConfig = WIZARD_STEPS[store.currentStep];

  /**
   * Check if can proceed to next step
   */
  const canProceed = (): boolean => {
    switch (store.currentStep) {
      case MonitorCreationStep.BASIC_INFO:
        // Name must match backend validation: alphanumeric, underscores, and hyphens only
        return !!(
          store.name &&
          store.name.length >= 3 &&
          store.name.length <= 100 &&
          /^[a-zA-Z0-9_-]+$/.test(store.name) &&
          store.dataset_id
        );
      case MonitorCreationStep.DIRECTORY_CONFIG:
        return !!(store.directory_path && store.directoryValidated);
      case MonitorCreationStep.PARTITION_MAPPING:
        // Partition mapping is optional if dataset has no partitions
        // The component handles its own validation
        return store.partitionMappingsValidated || store.partition_mappings.length === 0;
      case MonitorCreationStep.FILE_PATTERNS:
        return store.file_patterns.include_patterns.length > 0;
      case MonitorCreationStep.SCHEDULE:
        return !!(store.scan_frequency && store.monitor_type);
      case MonitorCreationStep.REVIEW:
        return false; // Review step handles its own submission
      default:
        return false;
    }
  };

  /**
   * Handle next button click
   */
  const handleNext = useCallback(() => {
    if (canProceed()) {
      store.nextStep();
    }
  }, [store]);

  /**
   * Handle previous button click
   */
  const handlePrevious = useCallback(() => {
    store.previousStep();
  }, [store]);

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    setShowCancelDialog(true);
  }, []);

  /**
   * Confirm cancellation
   */
  const handleConfirmCancel = useCallback(() => {
    store.resetWizard();
    navigate('/monitors');
  }, [store, navigate]);

  /**
   * Render step content
   */
  const renderStepContent = () => {
    switch (store.currentStep) {
      case MonitorCreationStep.BASIC_INFO:
        return <BasicInfoStep />;
      case MonitorCreationStep.DIRECTORY_CONFIG:
        return <DirectoryConfigStep />;
      case MonitorCreationStep.PARTITION_MAPPING:
        return <PartitionMappingStep />;
      case MonitorCreationStep.FILE_PATTERNS:
        return <FilePatternsStep />;
      case MonitorCreationStep.SCHEDULE:
        return <ScheduleConfigStep />;
      case MonitorCreationStep.REVIEW:
        return <MonitorReviewStep />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold">{currentStepConfig.title}</h2>
            <p className="text-muted-foreground">{currentStepConfig.description}</p>
          </div>
          <Button variant="ghost" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">
              Step {store.currentStep + 1} of {WIZARD_STEPS.length}
            </span>
            <span className="text-muted-foreground">
              {Math.round(progressPercent)}% complete
            </span>
          </div>
          <Progress value={progressPercent} className="w-full" />
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-between">
          {WIZARD_STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    transition-colors cursor-pointer
                    ${
                      index < store.currentStep
                        ? 'bg-primary text-primary-foreground'
                        : index === store.currentStep
                        ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                        : 'bg-muted text-muted-foreground'
                    }
                  `}
                  onClick={() => index < store.currentStep && store.goToStep(index)}
                  title={index < store.currentStep ? 'Click to return to this step' : undefined}
                >
                  {index + 1}
                </div>
                <span
                  className={`
                    mt-2 text-xs text-center
                    ${index === store.currentStep ? 'font-medium' : 'text-muted-foreground'}
                  `}
                >
                  {step.title}
                </span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div
                  className={`
                    flex-1 h-0.5 mx-2
                    ${index < store.currentStep ? 'bg-primary' : 'bg-muted'}
                  `}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[500px]">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={store.currentStep === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {store.currentStep < WIZARD_STEPS.length - 1 && (
          <Button onClick={handleNext} disabled={!canProceed()}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Monitor Creation?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel? All progress will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Continue Editing
            </Button>
            <Button variant="destructive" onClick={handleConfirmCancel}>
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
